import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { buildLinkedOptions, pruneSelections } from "../lib/linkedFilters";

const EMPTY_VALUE = { client_names: [], site_ids: [], guard_ids: [] };

/**
 * Drop-in linked Client / Site / Officer filter row.
 *
 * Renders 3 searchable multi-select checkbox dropdowns whose options
 * cross-narrow each other (Client ↔ Site direct, Officer ↔ Site via shifts).
 *
 * Props:
 *   value: { client_names: string[], site_ids: string[], guard_ids: string[] }
 *   onChange: (next) => void
 *   show?: { client?: boolean, site?: boolean, guard?: boolean }   (default all true)
 *   testIdPrefix?: string (default 'linked-filter')
 *   data?: { clients?, sites?, guards?, shifts? }   (skip internal fetches if provided)
 *   className?: string
 */
export function LinkedEntityFilters({
  value,
  onChange,
  show = { client: true, site: true, guard: true },
  testIdPrefix = "linked-filter",
  data,
  className = "",
}) {
  const sel = value || EMPTY_VALUE;
  const [sites, setSites] = useState(data?.sites || []);
  const [guards, setGuards] = useState(data?.guards || []);
  const [shifts, setShifts] = useState(data?.shifts || []);
  // iter332 — Track per-fetch completion so `pruneSelections` only fires
  // after every reference set has settled. A partially-loaded state (e.g.
  // /sites resolved but /shifts still in flight) would otherwise compute
  // empty guardOptions and wipe pre-selected guard_ids, cascading down.
  const [loaded, setLoaded] = useState({
    sites: !!data?.sites,
    guards: !!data?.guards,
    shifts: !!data?.shifts,
  });

  // Self-fetch reference data (skipped if a prop list is supplied)
  useEffect(() => {
    if (!data?.sites) {
      api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {})
        .finally(() => setLoaded((l) => ({ ...l, sites: true })));
    }
    if (!data?.guards && show.guard) {
      api.get("/guards").then((r) => setGuards(r.data || [])).catch(() => {})
        .finally(() => setLoaded((l) => ({ ...l, guards: true })));
    } else if (!show.guard) {
      setLoaded((l) => (l.guards ? l : { ...l, guards: true }));
    }
    if (!data?.shifts && (show.guard || show.site)) {
      api.get("/shifts").then((r) => setShifts(r.data || [])).catch(() => {})
        .finally(() => setLoaded((l) => ({ ...l, shifts: true })));
    } else if (!show.guard && !show.site) {
      setLoaded((l) => (l.shifts ? l : { ...l, shifts: true }));
    }
  }, [data, show.guard, show.site]);

  // iter332 — Derive `clients` synchronously from `sites` (was a separate
  // useEffect + state, which caused a one-render window where sites was
  // populated but clients was still []. During that window, the
  // pruneSelections effect below would wipe any pre-selected client
  // filter because clientOptions was momentarily empty.)
  const clients = useMemo(() => {
    if (data?.clients) return data.clients;
    return Array.from(new Set((sites || []).map((s) => s.client_name).filter(Boolean))).sort();
  }, [sites, data?.clients]);

  const options = useMemo(
    () => buildLinkedOptions({ clients, sites, guards, shifts, selected: sel }),
    [clients, sites, guards, shifts, sel]
  );

  // Self-heal: if selections fall out of scope after others change, drop
  // them. iter332 — Gate on ALL reference fetches completing so a
  // half-loaded state (e.g. /sites in, /shifts still pending) doesn't
  // wipe pre-selected officer/site chips.
  const referenceLoaded = loaded.sites && loaded.guards && loaded.shifts;
  useEffect(() => {
    if (!referenceLoaded) return;
    const pruned = pruneSelections(sel, options);
    if (
      pruned.client_names !== sel.client_names ||
      pruned.site_ids !== sel.site_ids ||
      pruned.guard_ids !== sel.guard_ids
    ) {
      onChange(pruned);
    }
  }, [options, sel, onChange, referenceLoaded]);

  const setDim = (key, next) => onChange({ ...sel, [key]: next });

  return (
    <div className={`grid md:grid-cols-3 gap-3 ${className}`} data-testid={`${testIdPrefix}-row`}>
      {show.client && (
        <div>
          <span className="label-mute block mb-1.5">Client</span>
          <MultiSelectDropdown
            testId={`${testIdPrefix}-client`}
            label="All clients"
            options={options.clientOptions}
            selected={sel.client_names}
            onChange={(v) => setDim("client_names", v)}
            searchable
            triggerClassName="w-full"
            emptyHint="No clients match"
          />
        </div>
      )}
      {show.site && (
        <div>
          <span className="label-mute block mb-1.5">
            Site
            {sel.client_names.length > 0 && <span className="text-[#0A84FF] ml-1.5">· linked</span>}
            {sel.guard_ids.length > 0 && sel.client_names.length === 0 && <span className="text-[#0A84FF] ml-1.5">· linked</span>}
          </span>
          <MultiSelectDropdown
            testId={`${testIdPrefix}-site`}
            label="All sites"
            options={options.siteOptions}
            selected={sel.site_ids}
            onChange={(v) => setDim("site_ids", v)}
            searchable
            triggerClassName="w-full"
            emptyHint="No sites match"
          />
        </div>
      )}
      {show.guard && (
        <div>
          <span className="label-mute block mb-1.5">
            Officer
            {(sel.site_ids.length > 0 || sel.client_names.length > 0) && <span className="text-[#0A84FF] ml-1.5">· linked</span>}
          </span>
          <MultiSelectDropdown
            testId={`${testIdPrefix}-guard`}
            label="All "
            options={options.guardOptions}
            selected={sel.guard_ids}
            onChange={(v) => setDim("guard_ids", v)}
            searchable
            triggerClassName="w-full"
            emptyHint="No  match"
          />
        </div>
      )}
    </div>
  );
}

export default LinkedEntityFilters;
